import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, Mail, CreditCard, User, Mountain } from 'lucide-react'
import Drawer from './Drawer'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'

const EMPTY = { name: '', email: '', rfid: '', mine_id: '' }

export default function AdminEquipe() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { operators, mines, adminError, reloadAdmin } = useWS()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const mineName = (id) => mines.find(m => m.id === id)?.name

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(null); setOpen(true) }
  const openEdit = (o) => {
    setEditingId(o.id)
    setForm({ name: o.name || '', email: o.email || '', rfid: o.rfid || '', mine_id: o.mine_id ?? '' })
    setError(null); setOpen(true)
  }

  // O RFID resolve o login na cabine e não tem índice único no banco: dois operadores
  // com o mesmo cartão resolvem de forma imprevisível. A checagem aqui é contra a lista
  // já carregada, então não substitui a restrição no banco, mas pega o caso comum.
  const rfidClash = (rfid) => {
    const norm = String(rfid || '').replace(/^0+(?=\d)/, '')
    if (!norm) return null
    return operators.find(o => o.id !== editingId && String(o.rfid || '').replace(/^0+(?=\d)/, '') === norm) || null
  }

  const save = async (e) => {
    e.preventDefault()
    const clash = rfidClash(form.rfid)
    if (clash) { setError(t('admin:team.rfid_duplicate', { name: clash.name })); return }
    setSaving(true); setError(null)
    try {
      // zeros à esquerda fora ("000" vira "0"): o protocolo novo manda o cartão como uint64
      const body = { ...form, rfid: form.rfid.replace(/^0+(?=\d)/, ''), mine_id: form.mine_id === '' ? null : Number(form.mine_id) }
      const url = editingId ? `/api/operators/${editingId}` : '/api/operators'
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!res.ok) throw await apiError(res)
      setOpen(false); reloadAdmin()
    } catch (err) { setError(err.message) } finally { setSaving(false) }
  }

  const remove = (o) => ask({
    title: t('admin:team.delete_title'),
    message: t('admin:team.delete_message', { name: o.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/operators/${o.id}`); reloadAdmin() },
  })

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:team.count', { count: operators.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:team.new')}
        </button>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} />
      ) : operators.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:team.empty_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {operators.map(o => (
            <div key={o.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center text-sm font-semibold shrink-0">
                  {(o.name || '?').trim().slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{o.name}</p>
                  {o.email && <p className="text-xs text-slate-400 dark:text-slate-500 truncate flex items-center gap-1"><Mail size={11} /> {o.email}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button disabled={!mayEdit} onClick={() => openEdit(o)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                  <button disabled={!mayDelete} onClick={() => remove(o)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <CreditCard size={12} className="text-slate-400 dark:text-slate-500" />
                {o.rfid ? <span className="font-mono tabular-nums">{o.rfid}</span> : <span className="text-slate-300 dark:text-slate-600">{t('admin:team.no_rfid')}</span>}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Mountain size={12} className="text-slate-400 dark:text-slate-500" />
                {mineName(o.mine_id) || <span className="text-slate-300 dark:text-slate-600">{t('admin:team.no_mine')}</span>}
              </p>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? t('admin:team.edit') : t('admin:team.new')}
        subtitle={t('admin:team.subtitle')}
        icon={
          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
            <User size={18} />
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
            <button type="submit" form="operator-form" disabled={saving} className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
              {saving ? t('admin:actions.saving') : editingId ? t('common:actions.save') : t('admin:actions.register')}
            </button>
          </div>
        }
      >
        <form id="operator-form" onSubmit={save} className="px-5 py-4 space-y-4">
          <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:team.fields.name')}</span>
            <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('admin:team.fields.name_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>

          <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:team.fields.email')}</span>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder={t('admin:team.fields.email_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>

          <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:team.fields.rfid')}</span>
            <input value={form.rfid} inputMode="numeric" onChange={e => setForm({ ...form, rfid: e.target.value.replace(/\D/g, '') })} placeholder={t('admin:team.fields.rfid_ph')} className="mt-1 w-full text-sm font-mono tabular-nums border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
            <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">{t('admin:team.fields.rfid_help')}</span></label>

          <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:team.fields.mine')}</span>
            <select value={form.mine_id} onChange={e => setForm({ ...form, mine_id: e.target.value })} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
              <option value="">{t('admin:team.fields.no_mine_option')}</option>
              {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select></label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </form>
      </Drawer>
      {confirmDialog}
    </div>
  )
}
